import React, { useEffect, useState } from 'react';
import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Key, UserSwitch, X } from 'phosphor-react';
import { toast } from 'sonner';
import { DuracaoConfig, SolicitarAcessoModal } from '../../components/SolicitarAcessoModal';
import { ISolicitacaoAcesso, useSolicitacaoAcesso } from '../../hooks/useSolicitacaoAcesso';
import { useSolicitacaoTokens } from '../../hooks/useSolicitacaoTokens';
import { useDialog } from '../../providers/dialog';
import { quandoFoi } from './acessoApi';
import { AZUL, AZUL_ESCURO, Etiqueta, Eyebrow, MONO, TEXTO, TEXTO_SUAVE } from './ui';

/**
 * Acesso delegado à conta de um colaborador (só administrador): pedir, acompanhar a aprovação,
 * copiar o token para entrar como ele e revogar. Mesmo fluxo da antiga lista de usuários.
 */
export function AcessoAConta({
  usuario,
}: {
  usuario: { id: number; nome: string; email: string; imagem_url?: string };
}) {
  const queryClient = useQueryClient();
  const showDialog = useDialog();
  const { criarSolicitacao, getMinhasSolicitacoes, cancelarSolicitacao } = useSolicitacaoAcesso();
  const { getToken, saveToken, removeToken, loadTokensFromStorage } = useSolicitacaoTokens();
  const [modal, setModal] = useState<{ aberto: boolean; reenvio: boolean }>({
    aberto: false,
    reenvio: false,
  });

  const { data, refetch } = useQuery({
    queryKey: ['minhas-solicitacoes'],
    queryFn: getMinhasSolicitacoes,
    refetchInterval: 60000, // reserva: o WebSocket avisa na hora
    refetchOnWindowFocus: true,
  });
  const solicitacoes: ISolicitacaoAcesso[] = data?.data ?? [];
  const solicitacao = solicitacoes.find(
    s => s.usuario_alvo_id === usuario.id && (s.status === 'pendente' || s.status === 'aprovado'),
  );
  const token =
    solicitacao?.status === 'aprovado'
      ? solicitacao.token_acesso || getToken(solicitacao.id)
      : undefined;

  // o token só vem em algumas respostas: guarda para continuar aparecendo
  useEffect(() => {
    if (
      solicitacao?.status === 'aprovado' &&
      solicitacao.token_acesso &&
      getToken(solicitacao.id) !== solicitacao.token_acesso
    )
      saveToken(solicitacao.id, solicitacao.token_acesso);
  }, [solicitacao, getToken, saveToken]);

  useEffect(() => {
    const aoReceber = () => {
      loadTokensFromStorage();
      refetch();
    };
    window.addEventListener('token-recebido', aoReceber as EventListener);
    return () => window.removeEventListener('token-recebido', aoReceber as EventListener);
  }, [loadTokensFromStorage, refetch]);

  const atualizar = () => {
    queryClient.invalidateQueries({ queryKey: ['minhas-solicitacoes'] });
    queryClient.invalidateQueries({ queryKey: ['solicitacoes-pendentes'] });
  };

  const criar = useMutation({
    mutationFn: (payload: {
      usuario_alvo_id: number;
      duracao_horas?: number;
      dt_expiracao?: string;
    }) => criarSolicitacao(payload),
    onSuccess: () => {
      refetch();
      atualizar();
      setModal({ aberto: false, reenvio: false });
      toast.success(`Pedido enviado: ${usuario.nome} recebe um aviso para aprovar.`);
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail || 'Não foi possível pedir o acesso'),
  });
  const cancelar = useMutation({
    mutationFn: (id: number) => cancelarSolicitacao(id),
    onSuccess: (_, id) => {
      removeToken(id);
      atualizar();
    },
  });

  const pedirDeNovo = async () => {
    const { data: atual } = await refetch();
    const ja = (atual?.data ?? []).find(
      s => s.usuario_alvo_id === usuario.id && s.status === 'aprovado',
    );
    if (ja) {
      toast.info(`${usuario.nome} já aprovou o acesso.`);
      return;
    }
    setModal({ aberto: true, reenvio: true });
  };

  const copiar = (t: string) => {
    navigator.clipboard.writeText(t);
    toast.success('Token copiado');
  };

  const botaoPrincipal = {
    size: 'sm' as const,
    borderRadius: '999px',
    color: 'white',
    bg: `linear-gradient(180deg, ${AZUL}, ${AZUL_ESCURO})`,
    _hover: { bg: AZUL_ESCURO },
  };

  let conteudo: React.ReactNode;
  if (solicitacao?.status === 'aprovado' && token) {
    conteudo = (
      <Flex direction="column" gap="10px">
        <Flex align="center" gap="8px" wrap="wrap">
          <Etiqueta tom="sucesso">Acesso aprovado</Etiqueta>
          {solicitacao.dt_expiracao && (
            <Text fontSize="12.5px" color={TEXTO_SUAVE}>
              vale até {quandoFoi(solicitacao.dt_expiracao)}
            </Text>
          )}
        </Flex>
        <Flex
          align="center"
          gap="8px"
          p="8px 8px 8px 14px"
          borderRadius="12px"
          bg="rgba(248,250,252,.9)"
          boxShadow="inset 0 0 0 1px rgba(15,23,42,.08)"
        >
          <Text fontFamily={MONO} fontSize="13px" fontWeight={600} flex={1} wordBreak="break-all">
            {token}
          </Text>
          <Button
            size="sm"
            variant="ghost"
            borderRadius="999px"
            leftIcon={<Copy size={14} />}
            onClick={() => copiar(token)}
          >
            Copiar
          </Button>
        </Flex>
        <Flex align="center" justify="space-between" gap="8px" wrap="wrap">
          <Text fontSize="12.5px" color={TEXTO_SUAVE}>
            Para entrar como ele, faça login com o e-mail {usuario.email} e este token no lugar da
            senha.
          </Text>
          <Button
            size="sm"
            variant="ghost"
            borderRadius="999px"
            color="#991B1B"
            leftIcon={<X size={14} />}
            isLoading={cancelar.isPending}
            onClick={() =>
              showDialog({
                status: 'warning',
                title: 'Revogar acesso',
                description: `O token para entrar na conta de ${usuario.nome} deixa de valer na hora.`,
                cancelLabel: 'Cancelar',
                async onConfirm() {
                  try {
                    await cancelar.mutateAsync(solicitacao.id);
                    toast.success('Acesso revogado');
                  } catch {
                    toast.error('Não foi possível revogar o acesso');
                  }
                },
              })
            }
          >
            Revogar
          </Button>
        </Flex>
      </Flex>
    );
  } else if (solicitacao?.status === 'aprovado') {
    conteudo = (
      <Flex
        direction="column"
        gap="8px"
        p="12px 14px"
        borderRadius="14px"
        bg="rgba(254,243,199,.55)"
        boxShadow="inset 0 0 0 1px #FDE68A"
      >
        <Text fontSize="13px" color="#92400E">
          {usuario.nome} aprovou, mas o token não está neste navegador (foi aprovado em outro
          computador ou antes da atualização do sistema). Peça de novo para receber um token.
        </Text>
        <Button
          alignSelf="flex-start"
          size="sm"
          borderRadius="999px"
          variant="outline"
          bg="white"
          isLoading={criar.isPending || cancelar.isPending}
          onClick={() =>
            showDialog({
              status: 'warning',
              title: 'Pedir acesso de novo',
              description: `O pedido atual é cancelado e ${usuario.nome} recebe um novo pedido para aprovar.`,
              cancelLabel: 'Cancelar',
              async onConfirm() {
                try {
                  await cancelar.mutateAsync(solicitacao.id);
                } catch {
                  // segue mesmo sem conseguir cancelar o antigo
                }
                await criar.mutateAsync({ usuario_alvo_id: usuario.id });
              },
            })
          }
        >
          Pedir de novo
        </Button>
      </Flex>
    );
  } else if (solicitacao?.status === 'pendente') {
    conteudo = (
      <Flex align="center" justify="space-between" gap="10px" wrap="wrap">
        <Box>
          <Etiqueta tom="aviso">Aguardando aprovação</Etiqueta>
          <Text fontSize="12.5px" color={TEXTO_SUAVE} mt="6px">
            Pedido feito {quandoFoi(solicitacao.dt_criacao)}. {usuario.nome} precisa aprovar no
            aviso que aparece para ele.
          </Text>
        </Box>
        <Flex gap="6px">
          <Button size="sm" variant="outline" bg="white" borderRadius="999px" onClick={pedirDeNovo}>
            Reenviar
          </Button>
          <Button
            size="sm"
            variant="ghost"
            borderRadius="999px"
            color="#991B1B"
            isLoading={cancelar.isPending}
            onClick={() =>
              showDialog({
                status: 'warning',
                title: 'Cancelar pedido',
                description: `Cancelar o pedido de acesso à conta de ${usuario.nome}?`,
                cancelLabel: 'Voltar',
                async onConfirm() {
                  await cancelar.mutateAsync(solicitacao.id);
                  toast.success('Pedido cancelado');
                },
              })
            }
          >
            Cancelar pedido
          </Button>
        </Flex>
      </Flex>
    );
  } else {
    conteudo = (
      <Flex align="center" justify="space-between" gap="10px" wrap="wrap">
        <Text fontSize="13px" color={TEXTO_SUAVE} flex="1 1 260px">
          Para ver o sistema como {usuario.nome} vê, peça acesso à conta. Ele recebe um aviso e
          precisa aprovar; você escolhe por quanto tempo vale.
        </Text>
        <Button
          {...botaoPrincipal}
          leftIcon={<UserSwitch size={14} />}
          onClick={() => setModal({ aberto: true, reenvio: false })}
        >
          Pedir acesso
        </Button>
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="12px">
      <Flex align="center" gap="8px">
        <Key size={16} color={TEXTO} />
        <Text fontSize="16px" fontWeight={650} color={TEXTO}>
          Acesso à conta
        </Text>
        <Box ml="auto">
          <Eyebrow>SÓ ADMINISTRADOR</Eyebrow>
        </Box>
      </Flex>
      {conteudo}
      <SolicitarAcessoModal
        isOpen={modal.aberto}
        onClose={() => setModal({ aberto: false, reenvio: false })}
        onConfirm={async (duracao: DuracaoConfig) => {
          const payload: {
            usuario_alvo_id: number;
            duracao_horas?: number;
            dt_expiracao?: string;
          } = { usuario_alvo_id: usuario.id };
          if (duracao.tipo === 'horas' && duracao.horas) payload.duracao_horas = duracao.horas;
          else if (duracao.tipo === 'data' && duracao.dataExpiracao)
            payload.dt_expiracao = new Date(`${duracao.dataExpiracao}T23:59:59`).toISOString();
          await criar.mutateAsync(payload);
        }}
        usuario={usuario}
        isLoading={criar.isPending}
        isReenvio={modal.reenvio}
      />
    </Flex>
  );
}
